import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PaddleProvider } from './contexts/PaddleContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './components/providers/theme-provider';
import { GlobalErrorBoundary } from './components/ErrorBoundary';
import DashboardLayout from './components/layouts/DashboardLayout';
import PublicLayout from './components/layouts/PublicLayout';
import PublicReportLayout from './components/layouts/PublicReportLayout';
import AuthLayout from './components/layouts/AuthLayout';
import { AdminLayout } from './components/layouts/AdminLayout';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { ImpersonationBanner } from './components/ui/ImpersonationBanner';
import { AuthSplashScreen } from './components/dashboard/loading';
import { PublicAnalytics } from './lib/analytics/PublicAnalytics';
import { savePostLoginRedirect } from './lib/postLoginRedirect';

const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Signup = lazy(() => import('./pages/Signup'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Issues = lazy(() => import('./pages/Issues'));
const Notifications = lazy(() => import('./pages/Notifications'));
const IssueDetail = lazy(() => import('./pages/IssueDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const TrelloOAuthCallback = lazy(() => import('./pages/TrelloOAuthCallback'));
const Billing = lazy(() => import('./pages/Billing'));
const Profile = lazy(() => import('./pages/Profile'));
const Repositories = lazy(() => import('./pages/Repositories'));
const RepositoryDetails = lazy(() => import('./pages/RepositoryDetails'));
const Advisor = lazy(() => import('./pages/Advisor'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportDetail = lazy(() => import('./pages/ReportDetail'));
const PublicReportView = lazy(() => import('./pages/PublicReportView'));
const Scans = lazy(() => import('./pages/Scans'));
const CreateOrganization = lazy(() => import('./pages/CreateOrganization'));
const TeamMembers = lazy(() => import('./pages/TeamMembers'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Docs = lazy(() => import('./pages/Docs'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Changelog = lazy(() => import('./pages/Changelog'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const WebsiteAnalytics = lazy(() => import('./pages/admin/WebsiteAnalytics'));
const Activation = lazy(() => import('./pages/admin/Activation'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const OrgManagement = lazy(() => import('./pages/admin/OrgManagement'));
const SAMLMasterView = lazy(() => import('./pages/admin/SAMLMasterView'));
const Prospecting = lazy(() => import('./pages/admin/Prospecting'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const NotFound = lazy(() => import('./pages/NotFound'));
const TechnicalDebtScanner = lazy(() => import('./pages/TechnicalDebtScanner'));
const CiCdQualityGates = lazy(() => import('./pages/CiCdQualityGates'));
const SampleReport = lazy(() => import('./pages/SampleReport'));
const McpAuthorize = lazy(() => import('./pages/McpAuthorize'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AuthSplashScreen isLoading={loading}><div /></AuthSplashScreen>;
  }

  if (!user) {
    // Pages that must resume after sign-in (the MCP consent page) are
    // remembered; savePostLoginRedirect ignores every other path.
    savePostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthSplashScreen isLoading={loading}><div /></AuthSplashScreen>;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthSplashScreen isLoading={loading}><div /></AuthSplashScreen>;
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Layout wrappers that apply route protection
const ProtectedDashboardLayout: React.FC = () => (
  <ProtectedRoute>
    <DashboardLayout />
  </ProtectedRoute>
);

const ProtectedAdminLayout: React.FC = () => (
  <AdminRoute>
    <AdminLayout />
  </AdminRoute>
);

const PublicAuthLayout: React.FC = () => (
  <PublicRoute>
    <AuthLayout />
  </PublicRoute>
);

const NavigateWithLog = ({ to }: { to: string }) => {
  return <Navigate to={to} replace />;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <PaddleProvider>
              <ConfirmProvider>
                <GlobalErrorBoundary>
                  <SonnerToaster />
                  <ImpersonationBanner />
                  <Suspense fallback={<AuthSplashScreen isLoading><div /></AuthSplashScreen>}>
                  <Routes>
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/es" element={<LandingPage />} />
                    <Route path="/de" element={<LandingPage />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/roadmap" element={<Roadmap />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/technical-debt-scanner" element={<TechnicalDebtScanner />} />
                    <Route path="/ci-cd-quality-gates" element={<CiCdQualityGates />} />
                    <Route path="/sample-report" element={<SampleReport />} />
                    <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                    <Route path="/legal/terms" element={<TermsOfService />} />
                    <Route path="/legal/refund" element={<RefundPolicy />} />

                    {/* Unknown URLs render the branded 404 inside the public
                        chrome. The static hosts serve build/404.html for these
                        with a real 404 status; in the SPA the client router
                        lands here after a soft navigation. */}
                    <Route path="*" element={<NotFound />} />
                  </Route>

                  {/* Authentication Routes */}
                  <Route element={<PublicAuthLayout />}>
                    <Route path="/login" element={<LoginForm />} />
                    <Route path="/register" element={<Signup />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/waitlist" element={<Waitlist />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                  </Route>

                  {/* Protected Dashboard Routes */}
                  <Route element={<ProtectedDashboardLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/trello-callback" element={<TrelloOAuthCallback />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/scans" element={<Scans />} />
                    <Route path="/repositories" element={<Repositories />} />
                    <Route path="/repositories/:id" element={<RepositoryDetails />} />
                    <Route path="/advisor" element={<Advisor />} />
                    <Route path="/advisor/:repositoryId" element={<Advisor />} />
                    <Route path="/advisor/:repositoryId/:conversationId" element={<Advisor />} />
                    <Route path="/issues" element={<Issues />} />
                    <Route path="/issues/:id" element={<IssueDetail />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/reports/:id" element={<ReportDetail />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/organizations/new" element={<CreateOrganization />} />
                    <Route path="/settings/team" element={<TeamMembers />} />
                  </Route>

                  {/* Remote MCP client consent (END-159): signed in, no dashboard chrome. */}
                  <Route path="/mcp/authorize" element={<ProtectedRoute><McpAuthorize /></ProtectedRoute>} />

                  {/* Public report share links — no auth guard: must be viewable
                      logged-out, and an owner previewing their own link while
                      logged in must not get redirected to /dashboard. */}
                  <Route element={<PublicReportLayout />}>
                    <Route path="/share/reports/:token" element={<PublicReportView />} />
                  </Route>

                  {/* Admin Routes */}
                  <Route path="/admin" element={<ProtectedAdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="website" element={<WebsiteAnalytics />} />
                    <Route path="activation" element={<Activation />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="organizations" element={<OrgManagement />} />
                    <Route path="saml" element={<SAMLMasterView />} />
                    <Route path="prospecting" element={<Prospecting />} />
                    <Route path="*" element={<NavigateWithLog to="/admin" />} />
                  </Route>

                  </Routes>
                  </Suspense>
                  {/* Mounted once for the whole application. Page views outside
                      the public allow-list are not sent. See
                      docs/analytics-event-taxonomy.md. */}
                  <PublicAnalytics />
                </GlobalErrorBoundary>
              </ConfirmProvider>
            </PaddleProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
